import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Technic, Workshop } from "../types";
import { technicService } from "../services/dataService";
import { PI, trLower, dash, useLayerEsc, useRestoreFocus, useScrollCursor } from "./PickerModal";

interface TechnicModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTechnic: (technic: Technic) => void;
  selectedTechnics?: Technic[];
  onClearAll?: () => void;
  workshops?: Workshop[]; // Atölye listesi
  onOpenCreateModal?: () => void; // Yeni teknik modal açma
}

const match = (t: Technic, q: string) =>
  trLower(t.name).includes(q) || trLower(t.category).includes(q) || trLower(t.description).includes(q) || trLower(t.workshop?.name).includes(q);

/* Çoklu seçim taslak üzerinde yürür (FirmMultiPicker ile aynı anlam): işaretler Seç'e basılınca çağırana aktarılır, Vazgeç / Esc / dışarı
   tıklama taslağı atar. Aktarım çağıranın onSelectTechnic (aç/kapa) işleviyle yalnız değişen teknikler için yapılır; hepsi
   kaldırıldıysa ve onClearAll verilmişse o kullanılır. */
const TechnicPicker: React.FC<Omit<TechnicModalProps, "isOpen">> = ({ onClose, onSelectTechnic, selectedTechnics = [], onClearAll, workshops = [], onOpenCreateModal }) => {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const [technics, setTechnics] = useState<Technic[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [draft, setDraft] = useState<Technic[]>(() => selectedTechnics);
  const [q, setQ] = useState("");
  const [onlySelected, setOnlySelected] = useState(false);
  const [cursor, setCursor] = useState(-1);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  useRestoreFocus();

  const loadTechnics = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      setTechnics(await technicService.getAll());
    } catch (error) {
      console.error("Teknikler yüklenirken hata:", error);
      setLoadError("Teknikler yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTechnics(); }, [loadTechnics]);
  useEffect(() => { searchRef.current?.focus(); }, []);

  const draftIds = useMemo(() => new Set(draft.map((t) => t.technicId)), [draft]);
  const filtered = useMemo(() => {
    const term = trLower(q.trim());
    return technics.filter((t) => (!onlySelected || draftIds.has(t.technicId)) && (!term || match(t, term)));
  }, [technics, q, onlySelected, draftIds]);

  useEffect(() => { setCursor(q.trim() ? 0 : -1); }, [q, onlySelected]);
  useScrollCursor(listRef, cursor);

  const toggle = (t: Technic) =>
    setDraft((d) => (d.some((x) => x.technicId === t.technicId) ? d.filter((x) => x.technicId !== t.technicId) : [...d, t]));

  const commit = () => {
    const before = new Set(selectedTechnics.map((t) => t.technicId));
    if (draft.length === 0 && before.size > 0 && onClearAll) { onClearAll(); return; }
    selectedTechnics.filter((t) => !draftIds.has(t.technicId)).forEach((t) => onSelectTechnic(t));
    draft.filter((t) => !before.has(t.technicId)).forEach((t) => onSelectTechnic(t));
  };
  const apply = () => { commit(); onClose(); };
  // Yeni teknik formu ayrı modal (çağıran açar, bu modal kapanır): o ana kadarki işaretler kaybolmasın diye önce aktarılır
  const openCreate = onOpenCreateModal ? () => { commit(); onOpenCreateModal(); } : undefined;

  useLayerEsc(onClose);

  const current = cursor >= 0 ? filtered[cursor] : undefined;
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); apply(); return; }
    if (!filtered.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(filtered.length - 1, c + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
    else if (e.key === "Enter" && !(e.target as HTMLElement).closest("button") && current) { e.preventDefault(); toggle(current); }
  };

  const wsName = (t: Technic) => t.workshop?.name || workshops.find((w) => w.workshopId === t.workshopId)?.name;
  const rowId = (i: number) => `${uid}-r${i}`;
  // Hiçbir teknikte dolu olmayan sütunlar gösterilmez
  const hasCat = technics.some((t) => t.category);
  const hasWs = technics.some((t) => wsName(t));
  const hasDesc = technics.some((t) => t.description);
  const n = draft.length;

  return (
    <div className="po-overlay center top pk-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="po-modal wide pk-modal" role="dialog" aria-modal="true" aria-labelledby={`${uid}-t`} onKeyDown={onKey}>
        <div className="po-modal-head">
          <div>
            <span className="po-kicker">Teknik</span>
            <h3 id={`${uid}-t`}>Teknik seç</h3>
            <p className="pk-sub">Birden fazla teknik işaretleyebilirsiniz; seçim <b>Seç</b> ile uygulanır.</p>
          </div>
          <button type="button" className="po-ib big" aria-label="Kapat" onClick={onClose}>{PI.close}</button>
        </div>

        <div className="po-modal-body pk-body">
          <div className="pk-tools">
            <div className="po-search">
              {PI.search}
              <input
                ref={searchRef}
                value={q}
                placeholder="Teknik adı, kategori, atölye veya açıklama ara"
                onChange={(e) => setQ(e.target.value)}
                role="combobox"
                aria-expanded="true"
                aria-controls={`${uid}-l`}
                aria-autocomplete="list"
                aria-activedescendant={current ? rowId(cursor) : undefined}
                aria-label="Teknik ara"
              />
              {q && <button type="button" className="po-ib" aria-label="Aramayı temizle" onClick={() => { setQ(""); searchRef.current?.focus(); }}>{PI.close}</button>}
            </div>
            <div className="po-chipgroup" role="radiogroup" aria-label="Gösterilen teknikler">
              <button type="button" role="radio" aria-checked={!onlySelected} className={!onlySelected ? "on" : ""} onClick={() => setOnlySelected(false)}>Tümü <i>{technics.length}</i></button>
              <button type="button" role="radio" aria-checked={onlySelected} className={onlySelected ? "on" : ""} onClick={() => setOnlySelected(true)}>Seçilenler <i>{n}</i></button>
            </div>
          </div>

          {n > 0 && (
            <div className="pk-chips" aria-label="Seçili teknikler">
              {draft.map((t) => (
                <span key={t.technicId} className="po-chip">
                  {t.name}
                  <button type="button" onClick={() => toggle(t)} aria-label={`${t.name} kaldır`}>{PI.close}</button>
                </span>
              ))}
            </div>
          )}

          {loadError ? (
            <div className="po-note err" role="alert">{loadError} · <button type="button" className="po-link" onClick={loadTechnics}>Yeniden dene</button></div>
          ) : loading ? (
            <div className="po-loading"><span className="po-spin" />Teknikler yükleniyor…</div>
          ) : filtered.length === 0 ? (
            <div className="po-blank small pk-blank">
              <span className="po-blank-icon">{PI.inbox}</span>
              <b>{q.trim() ? `"${q.trim()}" ile eşleşen teknik yok` : onlySelected ? "Henüz teknik işaretlenmedi" : "Henüz teknik eklenmemiş"}</b>
              {q.trim() ? "Aramayı değiştirin." : onlySelected ? "Tümü sekmesinden teknik işaretleyin." : "Teknikler Atölyeler ekranından da yönetilebilir."}
              {!q.trim() && !onlySelected && technics.length === 0 && openCreate && (
                <button type="button" className="po-btn primary" onClick={openCreate}>{PI.plus}İlk tekniği oluştur</button>
              )}
            </div>
          ) : (
            <div className="pk-list" ref={listRef}>
              <table className="po-table small pk-table" role="grid" id={`${uid}-l`} aria-label="Teknikler" aria-multiselectable="true">
                <thead>
                  <tr>
                    <th className="pk-ck" aria-label="Seçili" />
                    <th>Teknik</th>
                    {hasCat && <th className="po-hide-sm">Kategori</th>}
                    {hasWs && <th className="po-hide-sm">Atölye</th>}
                    {hasDesc && <th className="po-hide-md">Açıklama</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t, i) => {
                    const sel = draftIds.has(t.technicId);
                    const ws = wsName(t);
                    return (
                      <tr key={t.technicId} id={rowId(i)} data-i={i} aria-selected={sel} className={`${sel ? "sel" : ""} ${i === cursor ? "cur" : ""}`} onClick={() => { setCursor(i); toggle(t); }}>
                        <td className="pk-ck">
                          <span className="po-check"><input type="checkbox" checked={sel} readOnly tabIndex={-1} aria-hidden="true" /></span>
                        </td>
                        <td>
                          <span className="po-name">{t.name}</span>
                          {(t.category || ws) && <div className="po-sub po-only-sm">{[t.category, ws].filter(Boolean).join(" · ")}</div>}
                        </td>
                        {hasCat && <td className="po-hide-sm">{t.category || dash}</td>}
                        {hasWs && <td className="po-hide-sm">{ws || dash}</td>}
                        {hasDesc && <td className="po-hide-md">{t.description ? <span className="pk-desc" title={t.description}>{t.description}</span> : dash}</td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="po-modal-foot between">
          <span className="po-hint pk-foot-info">
            <span><b>{n}</b> seçili{n > 0 && <> · <button type="button" className="po-link" onClick={() => setDraft([])}>Temizle</button></>}</span>
            <span className="pk-keys po-hide-sm"><kbd>↑</kbd><kbd>↓</kbd> gezin · <kbd>Enter</kbd> işaretle · <kbd>Ctrl</kbd>+<kbd>Enter</kbd> uygula · <kbd>Esc</kbd> vazgeç</span>
          </span>
          <div className="po-foot-btns">
            {openCreate && <button type="button" className="po-btn" onClick={openCreate}>{PI.plus}Yeni teknik</button>}
            <button type="button" className="po-btn" onClick={onClose}>Vazgeç</button>
            <button type="button" className="po-btn primary" onClick={apply}>{n ? `Seç · ${n}` : "Seç"}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Teknik seçici (çoklu): arama, Tümü / Seçilenler süzgeci, onay kutulu tablo, klavye ile işaretleme; Seç ile uygulanır. */
const TechnicModal: React.FC<TechnicModalProps> = ({ isOpen, ...rest }) => (isOpen ? <TechnicPicker {...rest} /> : null);

export default TechnicModal;
